'use client';

/** Imports */
import superJson from 'superjson';
import {useState, type ReactNode} from 'react';
import {type AppRouter} from '@/server/api/root';
import {getQueryClient} from '@/trpc/query-client';
import {QueryClientProvider} from '@tanstack/react-query';
import {createTRPCContext} from '@trpc/tanstack-react-query';
import {createTRPCClient, httpBatchLink, loggerLink} from '@trpc/client';


/** Types */
type Props = Readonly<{
    children: ReactNode;
}>;


/**
 * The URL of the tRPC API endpoint.
 * This is relative to the current origin, so it will work in development and production without needing to change it.
 */
const TRPC_API_URL = '/api/trpc';
export const {TRPCProvider, useTRPC, useTRPCClient} = createTRPCContext<AppRouter>();


/**
 * The TRPCReactProvider component sets up the tRPC client and provides it to the rest of the app.
 *
 * @param props - The props for the component, which include the children to render inside the provider.
 * @returns A React component that provides the tRPC client to its children.
 */
export function TRPCReactProvider(props: Props) {
    const {children} = props;

    // Hooks
    const queryClient = getQueryClient();

    // State
    const [trpcClient] = useState(() => createTRPCClient<AppRouter>({
        links: [
            loggerLink({enabled: () => (process.env.NODE_ENV === 'development')}),
            httpBatchLink({
                url: TRPC_API_URL,
                transformer: superJson,
                fetch(url, options) {
                    return fetch(url, {...options, credentials: 'include'});
                },
            }),
        ],
    }));


    // Render
    return (
        <QueryClientProvider client={queryClient}>
            <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
                {children}
            </TRPCProvider>
        </QueryClientProvider>
    );
}
